/**
 * Horizon overrides for Shopify.actions:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 * - ELUXE: customer-facing UI stays in Spanish.
 */

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (drawer?.open) drawer.open();
      else window.location.href = Theme.routes.cart_url || '/cart';
    },
  });
}

if (window.Shopify?.actions) init();
else document.addEventListener('DOMContentLoaded', init, { once: true });

const exactUiTranslations = new Map([
  ['Page not found', 'Página no encontrada'],
  ['The link may be incorrect, or the page has been removed.', 'El enlace puede ser incorrecto o la página ya no está disponible.'],
  ['Continue shopping', 'Seguir comprando'],
  ['Discover something new', 'Descubre algo nuevo'],
  ['Collections', 'Colecciones'],
  ['Submit', 'Enviar'],
  ['SEARCH', 'BUSCAR'],
  ['SHOP', 'COMPRAR'],
  ['NEW', 'NOVEDADES'],
  ['CURATED LUXURY', 'LUJO SELECCIONADO'],
  ['THE EDIT / 01', 'LA EDICIÓN / 01'],
  ['THE ELUXE EDIT — 2026', 'LA EDICIÓN ELUXE — 2026'],
  ['DISCOVER', 'DESCUBRIR'],
  ['Home page', 'Inicio'],
]);

const variantTranslations = [
  [/\bAll Black Gold\b/gi, 'Negro y dorado'],
  [/\bAll Black (?:Sliver|Silver)\b/gi, 'Negro y plata'],
  [/\bRoseGold\b/gi, 'Oro rosa'],
  [/\bRose Gold\b/gi, 'Oro rosa'],
  [/\bDark\s+Blue\b/gi, 'Azul oscuro'],
  [/\bLight\s+Blue\b/gi, 'Azul claro'],
  [/\bMintBlue\b/gi, 'Azul menta'],
  [/\bGunColor\b/gi, 'Gris oscuro'],
  [/\bAll\s+Black\b/gi, 'Negro total'],
  [/\bAll\s+Gold\b/gi, 'Dorado total'],
  [/\bSliver\b/gi, 'Plata'],
  [/\bSilver\b/gi, 'Plata'],
  [/\bGolden\b/gi, 'Dorado'],
  [/\bGold\b/gi, 'Dorado'],
  [/\bBlack\b/gi, 'Negro'],
  [/\bBlue\b/gi, 'Azul'],
  [/\bWhite\b/gi, 'Blanco'],
  [/\bGreen\b/gi, 'Verde'],
  [/\bGrey\b/gi, 'Gris'],
  [/\bGray\b/gi, 'Gris'],
  [/\bRed\b/gi, 'Rojo'],
  [/\bBrown\b/gi, 'Marrón'],
  [/\bPink\b/gi, 'Rosa'],
  [/\bCoffee\b/gi, 'Café'],
  [/\bBronze\b/gi, 'Bronce'],
  [/\bRose\b/gi, 'Rosa'],
  [/\bLeather\b/gi, 'Cuero'],
  [/\bSteel\b/gi, 'Acero'],
  [/\bMen\b/gi, 'Hombre'],
  [/\bMan\b/gi, 'Hombre'],
  [/\bWomen\b/gi, 'Mujer'],
  [/\bWoman\b/gi, 'Mujer'],
];

const productTextTranslations = [
  [/\bDual Time\b/gi, 'doble huso horario'],
  [/\bBusiness\b/gi, 'Ejecutivo'],
  [/\bskeleton\b/gi, 'esqueleto'],
];

function replaceNodeText(node, replacements) {
  let value = node.nodeValue || '';
  for (const [pattern, replacement] of replacements) value = value.replace(pattern, replacement);
  if (value !== node.nodeValue) node.nodeValue = value;
}

function translateExactUi(root = document) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes = [];
  while (walker.nextNode()) nodes.push(walker.currentNode);
  for (const node of nodes) {
    const original = node.nodeValue || '';
    const trimmed = original.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim();
    if (!trimmed) continue;
    if (exactUiTranslations.has(trimmed)) {
      node.nodeValue = exactUiTranslations.get(trimmed);
      continue;
    }
    const bagMatch = trimmed.match(/^BAG\s*\((\d+)\)$/i);
    if (bagMatch) node.nodeValue = `CARRITO (${bagMatch[1]})`;
  }
  root.querySelectorAll?.('input[type="email"][aria-label="Email"]').forEach((input) => input.setAttribute('aria-label', 'Correo electrónico'));
  root.querySelectorAll?.('[aria-label="Primary"]').forEach((el) => el.setAttribute('aria-label', 'Navegación principal'));
  root.querySelectorAll?.('[aria-label="Utilities"]').forEach((el) => el.setAttribute('aria-label', 'Utilidades'));
  root.querySelectorAll?.('a[aria-label$=" home"]').forEach((el) => el.setAttribute('aria-label', el.getAttribute('aria-label').replace(/ home$/, ' inicio')));
}

function cleanVariantOptions(root = document) {
  const options = new Set();
  if (root.matches?.('variant-picker fieldset, variant-picker .variant-option')) options.add(root);
  root.querySelectorAll?.('variant-picker fieldset, variant-picker .variant-option').forEach((el) => options.add(el));
  for (const option of options) {
    const compactText = (option.textContent || '').replace(/\s+/g, ' ').trim().toLowerCase();
    if (compactText.startsWith('ships from') || compactText.startsWith('is customized')) {
      option.hidden = true;
      option.style.setProperty('display', 'none', 'important');
      option.setAttribute('aria-hidden', 'true');
      continue;
    }
    const walker = document.createTreeWalker(option, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => replaceNodeText(node, variantTranslations));
  }
}

function translateProductCopy(root = document) {
  const containers = root.querySelectorAll?.('.product-details, .product-card, .product-recommendations') || [];
  for (const container of containers) {
    const walker = document.createTreeWalker(container, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) nodes.push(walker.currentNode);
    nodes.forEach((node) => replaceNodeText(node, productTextTranslations));
  }
}

function addSpanishStyleOverride() {
  if (document.getElementById('eluxe-spanish-ui')) return;
  const style = document.createElement('style');
  style.id = 'eluxe-spanish-ui';
  style.textContent = 'body:has(.eluxe-product-scope-marker) .product-details>.group-block:before{content:"SELECCIÓN ELUXE"!important;}';
  document.head.appendChild(style);
}

function applySpanishUi() {
  addSpanishStyleOverride();
  translateExactUi(document);
  cleanVariantOptions(document);
  translateProductCopy(document);
}

function startSpanishUi() {
  applySpanishUi();
  let scheduled = false;
  const observer = new MutationObserver(() => {
    if (scheduled) return;
    scheduled = true;
    requestAnimationFrame(() => {
      scheduled = false;
      applySpanishUi();
    });
  });
  observer.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startSpanishUi, { once: true });
else startSpanishUi();
